import React from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, ArrowUpRight } from "lucide-react";
import { PageHero, Reveal, SectionHeader, CTABanner } from "@/components/ui-blocks";
import { SITE } from "@/data/site";

const CHANNELS = [
  {
    label: "Call",
    value: SITE.contact.phone,
    href: `tel:${SITE.contact.phone}`,
    icon: Phone,
    hint: "Fastest response — Mon–Fri, 7a–6p",
    testid: "contact-phone",
  },
  {
    label: "Email",
    value: SITE.contact.email,
    href: `mailto:${SITE.contact.email}`,
    icon: Mail,
    hint: "One business-day reply",
    testid: "contact-email",
  },
  {
    label: "Chat",
    value: "Message the team",
    href: "#",
    icon: MessageCircle,
    hint: "Use the chat bubble in the corner",
    testid: "contact-chat",
    onClick: (e) => {
      e.preventDefault();
      // Try to trigger LeadConnector chat widget if exposed on window
      const anyWin = window;
      if (anyWin.LC_API && typeof anyWin.LC_API.open_chat_window === "function") {
        anyWin.LC_API.open_chat_window();
        return;
      }
      // Fallback: click the widget's own launcher iframe/button
      const btn = document.querySelector('[data-testid="ChatBubbleContainer"]')
        || document.querySelector('iframe[title*="chat" i]')
        || document.querySelector('lc-chat-widget');
      if (btn && btn.click) btn.click();
    },
  },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact PHOS"
        title={["Start the", "conversation."]}
        sub="Call, email, or ping us in the chat bubble — a senior PM responds within one business day with a preliminary approach and next steps."
      />

      <section className="relative py-16 lg:py-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          {/* Left: contact info stack */}
          <aside className="lg:col-span-5">
            <SectionHeader eyebrow="Reach us" title={<>Three ways{"\n"}to talk.</>} />

            <div className="mt-10 space-y-4">
              {CHANNELS.map((c, i) => {
                const Icon = c.icon;
                return (
                  <Reveal key={c.label} delay={i * 0.08}>
                    <a
                      href={c.href}
                      onClick={c.onClick}
                      data-testid={c.testid}
                      className="group flex items-start gap-5 border border-white/10 hover:border-accent transition-colors duration-500 p-6 lg:p-7"
                    >
                      <div className="w-11 h-11 border border-white/15 flex items-center justify-center shrink-0 group-hover:border-accent group-hover:text-accent transition-colors duration-500">
                        <Icon size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="overline text-ink-400">{c.label}</div>
                        <div className="font-heading text-xl font-bold text-ink-50 mt-1 break-all group-hover:text-accent transition-colors duration-500">
                          {c.value}
                        </div>
                        <p className="text-ink-400 text-sm mt-2">{c.hint}</p>
                      </div>
                      <ArrowUpRight
                        size={18}
                        className="text-ink-600 group-hover:text-accent group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500 shrink-0"
                      />
                    </a>
                  </Reveal>
                );
              })}
            </div>

            <div className="mt-10 grid grid-cols-2 gap-4">
              <div className="border border-white/10 p-6">
                <div className="flex items-center gap-2 overline"><MapPin size={12} className="text-accent" /> Office</div>
                <p className="text-ink-200 text-sm mt-3 leading-relaxed">{SITE.contact.address}</p>
                <p className="text-ink-400 text-xs mt-1">Serving Southeast Michigan</p>
              </div>
              <div className="border border-white/10 p-6">
                <div className="flex items-center gap-2 overline"><Clock size={12} className="text-accent" /> Hours</div>
                <p className="text-ink-200 text-sm mt-3 leading-relaxed">{SITE.contact.hours}</p>
                <p className="text-ink-400 text-xs mt-1">On-call for emergencies</p>
              </div>
            </div>
          </aside>

          {/* Right: large map */}
          <div className="lg:col-span-7">
            <div className="relative border border-white/10 aspect-[4/3] lg:aspect-auto lg:h-full min-h-[520px] overflow-hidden">
              <iframe
                title="PHOS Management Office Map"
                className="absolute inset-0 w-full h-full grayscale contrast-125"
                loading="lazy"
                src="https://www.google.com/maps?q=12200+Sibley+Rd+Riverview+MI+48192&output=embed"
              />
              <div className="pointer-events-none absolute top-4 left-4 flex items-center gap-3 bg-obsidian-950/80 backdrop-blur border border-white/10 px-4 py-3">
                <span className="w-2 h-2 bg-accent block" />
                <div>
                  <div className="overline text-[10px]">PHOS Headquarters</div>
                  <div className="text-ink-50 text-sm font-heading font-bold">{SITE.contact.address}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABanner
        eyebrow="Prefer to talk?"
        heading={<>Give us a{"\n"}call today.</>}
        sub={`Dial ${SITE.contact.phone} or use the chat bubble in the corner — we respond fast.`}
        cta="Call PHOS"
        ctaHref={`tel:${SITE.contact.phone}`}
      />
    </div>
  );
}
